import { computed, ref } from 'vue'
import { Capacitor } from '@capacitor/core'
import { useAuthStore } from '@/stores/auth'
import { userHasAbility, userHasRole } from '@/utils/permissions'

/**
 * Shared iPhone Tap to Pay eligibility, communications, and setup authorization.
 */
export function useIphoneTopEligibility () {
  const authStore = useAuthStore()
  const wizardState = ref({
    showWizard: false,
    deviceReady: false,
    iphoneConfigReady: false,
    loading: false
  })

  const isIosNative = computed(() =>
    Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios'
  )

  const user = computed(() => authStore.user)

  const canAcceptIphoneTopTerms = computed(() => {
    const u = user.value
    if (!u) {
      return false
    }
    return userHasRole(u, 'owner')
      || userHasRole(u, 'manager')
      || userHasAbility(u, 'payments.iphone_top_setup')
  })

  const canOpenSetupWizard = computed(() => canAcceptIphoneTopTerms.value)

  const showCommunications = computed(() =>
    isIosNative.value && wizardState.value.showWizard === true
  )

  async function refreshWizardState () {
    if (!isIosNative.value) {
      wizardState.value = {
        showWizard: false,
        deviceReady: false,
        iphoneConfigReady: false,
        loading: false
      }
      return wizardState.value
    }
    wizardState.value = { ...wizardState.value, loading: true }
    try {
      const { getIphoneTopWizardEligibility } = await import('@/services/DejavooTop.js')
      const state = await getIphoneTopWizardEligibility()
      wizardState.value = {
        showWizard: state.showWizard === true,
        deviceReady: state.deviceReady === true,
        iphoneConfigReady: state.iphoneConfigReady === true,
        loading: false
      }
    } catch {
      wizardState.value = {
        showWizard: false,
        deviceReady: false,
        iphoneConfigReady: false,
        loading: false
      }
    }
    return wizardState.value
  }

  return {
    isIosNative,
    wizardState,
    showCommunications,
    canAcceptIphoneTopTerms,
    canOpenSetupWizard,
    refreshWizardState
  }
}
